import { Policy } from '../models/policy/Policy';

/**
 * Single source of truth for what a broker/insurer is allowed to change on
 * a policy at each point in its lifecycle. Callers (edit form field
 * disabling, save routing, banner messaging) all consume this so the
 * governance model stays consistent across surfaces — if the operator sees
 * a field greyed out on the edit page, the save route on that page cannot
 * accidentally accept a change to it, and the endorsement endpoint can
 * validate against the same list.
 *
 * States mirror the client CR's "Draft / Pending Approval / Active" model:
 *
 *  - draft:    policy created, no payment yet. Everything editable.
 *  - pending:  payment received, awaiting insurer decision. Only
 *              administrative edits allowed; premium/commission-affecting
 *              fields are hard-blocked. Broker must ask the insurer to
 *              reject the policy back to draft to change those.
 *  - active:   policy in force. Every save is an endorsement (audit-logged).
 *              Minor endorsements land in this release; major endorsements
 *              (the same fields that were hard-blocked in pending) are
 *              greyed out with a "future release" reason.
 *  - terminal: cancelled / declined / archived / expired. No edits.
 */
export type PolicyEditState = 'draft' | 'pending' | 'active' | 'terminal';

/**
 * Field keys the edit form gates against. Grouped by CR scope so future
 * additions (major endorsement release) can flip the gate rather than
 * add new keys. Kept as string literals rather than an enum so JSX prop
 * spreading stays terse — `permissions.fields.SUM_INSURED.editable`.
 */
export const POLICY_EDIT_FIELD = {
  // Administrative — always editable outside terminal states.
  CUSTOMER_NAME: 'CUSTOMER_NAME',
  CUSTOMER_PHONE: 'CUSTOMER_PHONE',
  CUSTOMER_ID: 'CUSTOMER_ID',
  CUSTOMER_DOCUMENTS: 'CUSTOMER_DOCUMENTS',
  DRIVER_DETAILS: 'DRIVER_DETAILS',
  CO_INSURED: 'CO_INSURED',
  VEHICLE_COLOR: 'VEHICLE_COLOR',
  VEHICLE_REGISTRATION_NUMBER: 'VEHICLE_REGISTRATION_NUMBER',

  // Premium/commission-affecting — hard-blocked on pending, major-endorsement
  // territory on active.
  SUM_INSURED: 'SUM_INSURED',
  COVER_TYPE: 'COVER_TYPE',
  VEHICLE_MAKE: 'VEHICLE_MAKE',
  VEHICLE_MODEL: 'VEHICLE_MODEL',
  VEHICLE_USAGE: 'VEHICLE_USAGE',
  POLICY_START_DATE: 'POLICY_START_DATE',
  POLICY_END_DATE: 'POLICY_END_DATE',
} as const;

export type PolicyEditFieldKey = typeof POLICY_EDIT_FIELD[keyof typeof POLICY_EDIT_FIELD];

export interface FieldGate {
  editable: boolean;
  /** Displayed as a tooltip / helper text next to a greyed-out control. */
  reason?: string;
  /**
   * True when the field is editable but the save must be routed through
   * the endorsement audit flow (Active state, minor endorsement fields).
   * Callers use this to swap the save handler / show the endorsement note
   * modal on submit.
   */
  requiresEndorsement?: boolean;
}

export interface PolicyEditPermissions {
  state: PolicyEditState;
  /**
   * Whole-policy read-only flag: no field on this policy is editable and
   * no save handler should fire. Terminal states set this true.
   */
  readOnly: boolean;
  /**
   * True when any edit on this policy — even administrative ones — must
   * be recorded as an endorsement. Callers use this to show the "changes
   * on this policy will be logged as an endorsement" banner and to route
   * the save.
   */
  saveAsEndorsement: boolean;
  fields: Record<PolicyEditFieldKey, FieldGate>;
  isEditable: (fieldKey: PolicyEditFieldKey) => boolean;
  reasonFor: (fieldKey: PolicyEditFieldKey) => string | undefined;
}

const PENDING_BLOCK_REASON =
  'Payment received. To change this, ask the insurer to reject the policy back to Draft.';

const ACTIVE_MAJOR_BLOCK_REASON =
  'This change requires a major endorsement, which is reserved for a future release. Contact support if urgent.';

const TERMINAL_REASON =
  'The policy is in a terminal state (cancelled, declined, archived, or expired) and cannot be edited.';

const ADMIN_FIELDS: PolicyEditFieldKey[] = [
  POLICY_EDIT_FIELD.CUSTOMER_NAME,
  POLICY_EDIT_FIELD.CUSTOMER_PHONE,
  POLICY_EDIT_FIELD.CUSTOMER_ID,
  POLICY_EDIT_FIELD.CUSTOMER_DOCUMENTS,
  POLICY_EDIT_FIELD.DRIVER_DETAILS,
  POLICY_EDIT_FIELD.CO_INSURED,
  POLICY_EDIT_FIELD.VEHICLE_COLOR,
  POLICY_EDIT_FIELD.VEHICLE_REGISTRATION_NUMBER,
];

const MAJOR_FIELDS: PolicyEditFieldKey[] = [
  POLICY_EDIT_FIELD.SUM_INSURED,
  POLICY_EDIT_FIELD.COVER_TYPE,
  POLICY_EDIT_FIELD.VEHICLE_MAKE,
  POLICY_EDIT_FIELD.VEHICLE_MODEL,
  POLICY_EDIT_FIELD.VEHICLE_USAGE,
  POLICY_EDIT_FIELD.POLICY_START_DATE,
  POLICY_EDIT_FIELD.POLICY_END_DATE,
];

const buildFieldMap = (
  perField: (fieldKey: PolicyEditFieldKey) => FieldGate,
): Record<PolicyEditFieldKey, FieldGate> => {
  const map = {} as Record<PolicyEditFieldKey, FieldGate>;
  for (const key of Object.values(POLICY_EDIT_FIELD)) {
    map[key] = perField(key);
  }
  return map;
};

const derivePolicyEditState = (policy: Policy | undefined): PolicyEditState => {
  if (!policy) return 'draft';
  if (policy.isCanceled?.() || policy.isDeclined?.() || policy.isExpired?.()) return 'terminal';
  if (policy.isApproved?.()) return 'active';
  if (policy.isPendingApproval?.() || policy.isInApproval?.() || policy.isInReview?.()) return 'pending';
  if (policy.isDraft?.()) return 'draft';
  // Default: unknown reviewStage → be conservative and treat as pending, so
  // fields that would need audit trail don't leak through untracked.
  return 'pending';
};

/**
 * On fleet policies the redesign moved SUM_INSURED, COVER_TYPE, START/END
 * DATE, VEHICLE_USAGE onto the individual Vehicles row rather than the
 * aggregate Policy. Each car is its own quote and cover window, so those
 * fields must remain editable per-vehicle even on Pending Approval /
 * Active fleets — the "major endorsement" gate only exists to guard the
 * aggregate policy's cover math, which no longer owns them for fleets.
 *
 * On single-vehicle policies the same fields still live on the parent
 * Policy so the lockdown applies as before.
 */
const isFleetPolicy = (policy: Policy | undefined): boolean => {
  if (!policy) return false;
  return Boolean((policy as any)?.fleetPolicy ?? (policy as any)?.isFleetPolicy);
};

export const getPolicyEditPermissions = (policy: Policy | undefined): PolicyEditPermissions => {
  const state = derivePolicyEditState(policy);
  const fleet = isFleetPolicy(policy);

  const perField = (fieldKey: PolicyEditFieldKey): FieldGate => {
    const isAdmin = ADMIN_FIELDS.includes(fieldKey);
    const isMajor = MAJOR_FIELDS.includes(fieldKey);
    // Fleet vehicles own their own SUM_INSURED / COVER_TYPE / dates
    // (see the per-vehicle redesign). Treat those as admin-level for the
    // fleet edit surface so the per-vehicle picker can drive them.
    const isMajorForThisPolicy = isMajor && !fleet;

    if (state === 'terminal') return { editable: false, reason: TERMINAL_REASON };
    if (state === 'draft') return { editable: true };

    if (state === 'pending') {
      if (isMajorForThisPolicy) return { editable: false, reason: PENDING_BLOCK_REASON };
      return { editable: true }; // admin fields + fleet-owned per-vehicle fields
    }

    // state === 'active'
    if (isMajorForThisPolicy) return { editable: false, reason: ACTIVE_MAJOR_BLOCK_REASON };
    // Admin fields (and, on fleets, the per-vehicle fields) on active are
    // editable but recorded as endorsements.
    return { editable: true, requiresEndorsement: isAdmin || (fleet && isMajor) };
  };

  const fields = buildFieldMap(perField);

  return {
    state,
    readOnly: state === 'terminal',
    saveAsEndorsement: state === 'active',
    fields,
    isEditable: (fieldKey) => fields[fieldKey]?.editable ?? false,
    reasonFor: (fieldKey) => fields[fieldKey]?.reason,
  };
};
